import React, { useEffect, useState, useCallback } from "react"
import Layout from "@/components/layout/Layout"
import MainHeading from "@/components/common/MainHeading"
import CommonTable, { type TableColumn } from "@/components/common/CommonTable"
import CommonFilter from "@/components/common/CommonFilter"
import TablePagination from "@/components/common/TablePagination"
import { AttendanceService } from "@/services/Attendance/AttendanceService"
import { ThrowError } from "@/api/callApi"
import { GetFilterUrl } from "@/utility/helper"
import { useLocation } from "react-router-dom"
import moment from "moment"
import type { IAttendanceLog } from "@/interface/attendance.interface"
import type { IFilter, IPageInfo } from "@/interface/common.interface"
import { useCurrentUser } from "@/hooks/useCurrentUser"

const LOG_TYPE_OPTIONS = [
    { label: "Check In", value: "CHECK_IN" },
    { label: "Check Out", value: "CHECK_OUT" },
]

const AttendanceLog: React.FC = () => {
    // const navigate = useNavigate()
    const location = useLocation();
    const { siteId } = useCurrentUser();
    const [logs, setLogs] = useState<IAttendanceLog[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [paginationData, setPaginationData] = useState<IPageInfo>({
        currentPage: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    })

    const getInitialDate = () => {
        if (location?.state?.date) {
            const dateStr = location.state.date;
            if (moment(dateStr, "DD-MM-YYYY", true).isValid()) {
                return moment(dateStr, "DD-MM-YYYY").format("YYYY-MM-DD");
            }
            if (moment(dateStr, "YYYY-MM-DD", true).isValid()) {
                return dateStr;
            }
            const parsed = moment(dateStr);
            if (parsed.isValid()) {
                return parsed.format("YYYY-MM-DD");
            }
        }
        return "";
    };

    const [filter, setFilter] = useState<IFilter>({
        search: "",
        type: "",
        date: getInitialDate(),
        attendanceId: location?.state?.attendanceId ?? "",
        // userId: userId,
        siteId: siteId
    })

    const handleSearchChange = useCallback((value: string) => {
        setFilter((prev) => ({ ...prev, search: value }))
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }, [])

    const handleFilterChange = useCallback((value: string, name: string) => {
        setFilter((prev) => ({ ...prev, [name]: value }))
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }, [])

    const handleSingleDateChange = useCallback((date: string) => {
        setFilter((prev) => ({ ...prev, date: date }))
        setPaginationData((prev) => ({ ...prev, currentPage: 1 }))
    }, [])

    const handleChangePageSize = (value: number) => {
        setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }))
    }

    const handleGetLogs = async (currentPage = paginationData.currentPage, currentLimit = paginationData.pageSize) => {
        setIsLoading(true)
        try {
            const queryParams = GetFilterUrl({ ...filter })
            const siteIdParam = siteId ? `&siteId=${siteId}` : "";
            const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${queryParams ? `&${queryParams}` : ""}${siteIdParam}`
            const response = await AttendanceService.getAttendanceLogs(PATH)
            if (response?.status && response?.data) {
                setLogs(response.data.attendanceLogs || [])
                setPaginationData((prev) => ({
                    ...prev,
                    total: response?.data?.pageInfo?.total || 0,
                    totalPages: response?.data?.pageInfo?.totalPages || 0,
                }))
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        handleGetLogs(paginationData.currentPage, paginationData.pageSize)
    }, [paginationData.currentPage, paginationData.pageSize, filter])

    const formatTime = (timeStr: string | null) => {
        if (!timeStr) return '--:--'
        if (timeStr.includes('T') || timeStr.includes('-')) {
            return moment.utc(timeStr).format('hh:mm A')
        }
        return moment(timeStr, 'HH:mm:ss').format('hh:mm A')
    }

    const columns: TableColumn<IAttendanceLog>[] = [
        {
            key: "srNo",
            header: "Sr No",
            render: (_item, index) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: "date",
            header: "Date",
            render: (item) => (
                <span className="font-semibold">{item?.date ? moment(item.date).format("DD-MM-YYYY") : "—"}</span>
            )
        },
        {
            key: "time",
            header: "Time",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.time ? formatTime(item.time) : "—"}</span>
            )
        },
        {
            key: "type",
            header: "Log Type",
            render: (item) => {
                const type = (item?.type || "CHECK_IN").toUpperCase()
                let typeClasses = "text-green-600 border-green-600 bg-green-50"
                if (type === "CHECK_OUT") {
                    typeClasses = "text-rose-600 border-rose-600 bg-rose-50"
                }
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold border rounded-full ${typeClasses}`}>
                        {type.replace("_", " ")}
                    </span>
                )
            }
        },
        {
            key: "createdAt",
            header: "Recorded At",
            render: (item) => (
                <span className="text-slate-500 text-sm font-medium">
                    {item?.createdAt ? moment(item.createdAt).format("DD-MM-YYYY hh:mm A") : "—"}
                </span>
            )
        }
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Attendance Logs"
                    isBackButtonVisible={true}
                // onClick={() => navigate()}
                />

                <CommonFilter
                    searchValue={filter.search || ""}
                    onSearchChange={handleSearchChange}
                    singleDate={filter.date || ""}
                    onSingleDateChange={handleSingleDateChange}
                    value={filter}
                    filterOptions={LOG_TYPE_OPTIONS}
                    filterName="type"
                    filterPlaceholder="Select Log Type"
                    onChange={handleFilterChange}
                />

                <CommonTable
                    columns={columns}
                    data={logs}
                    loading={isLoading}
                />

                <TablePagination
                    paginationData={paginationData}
                    setPaginationData={setPaginationData}
                    handleChangePageSize={handleChangePageSize}
                    isLoading={isLoading}
                />
            </div>
        </Layout>
    )
}

export default AttendanceLog